import { PaletteCategory, TViewCommonOption, TWidgetCommonOption } from '@libs/types';
import { ALIGNMENT_ENUM, TCoreOptions } from '../types/options';

const getValue = (value?: number | string, postfix?: string) => (value ? `${value}${postfix ?? ''}` : undefined);

const getMarginStyles = (options?: TWidgetCommonOption) => {
    const margin = options?.margin ?? {};
    return {
        marginTop: getValue(margin.top, 'px'),
        marginRight: getValue(margin.right, 'px'),
        marginBottom: getValue(margin.bottom, 'px'),
        marginLeft: getValue(margin.left, 'px'),
    };
};
export const getPaddingStyles = (options?: TCoreOptions) => {
    const padding = options?.padding ?? {};
    return {
        paddingTop: getValue(padding.top, 'px'),
        paddingRight: getValue(padding.right, 'px'),
        paddingBottom: getValue(padding.bottom, 'px'),
        paddingLeft: getValue(padding.left, 'px'),
    };
};

export const getCommonStyles = (options?: TWidgetCommonOption, isHidden = false, display: string | undefined = undefined) => {
    return {
        ...getMarginStyles(options),
        display: isHidden ? 'none' : display,
    };
};
export const getAlignment = (alignment?: ALIGNMENT_ENUM) => {
    if (!alignment) return {};
    const alignmentMap = {
        [ALIGNMENT_ENUM.TOP_LEFT]: {
            position: 'absolute',
            top: 0,
            left: 0,
        },
        [ALIGNMENT_ENUM.TOP_CENTER]: {
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
        },
        [ALIGNMENT_ENUM.TOP_RIGHT]: {
            position: 'absolute',
            top: 0,
            right: 0,
        },
        [ALIGNMENT_ENUM.CENTER_LEFT]: {
            position: 'absolute',
            top: '50%',
            left: 0,
        },
        [ALIGNMENT_ENUM.CENTER]: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translateX(-50%)',
        },
        [ALIGNMENT_ENUM.CENTER_RIGHT]: {
            position: 'absolute',
            top: '50%',
            right: 0,
        },
        [ALIGNMENT_ENUM.BOTTOM_LEFT]: {
            position: 'absolute',
            bottom: 0,
            left: 0,
        },
        [ALIGNMENT_ENUM.BOTTOM_CENTER]: {
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
        },
        [ALIGNMENT_ENUM.BOTTOM_RIGHT]: {
            position: 'absolute',
            bottom: 0,
            right: 0,
        },
    };
    return alignmentMap[alignment] ?? {};
};

export const mergeWithTheme = <T extends object>(themePalette: T, styleProps?: Partial<T> | null): T => {
    const filtered = Object.fromEntries(Object.entries(themePalette).filter(([, v]) => v !== undefined && v !== null));
    return { ...(styleProps ?? {}), ...filtered } as T;
};

export const getFontStyles = (palette?: PaletteCategory) => {
    const fontStyle = palette?.fontStyle;

    return {
        fontFamily: getValue(palette?.font),
        fontWeight: getValue(fontStyle?.fontWeight),
        fontSize: getValue(fontStyle?.fontSize, 'px'),
        lineHeight: getValue(fontStyle?.lineHeight, 'px'),
        letterSpacing: getValue(fontStyle?.letterSpacing, 'px'),
    };
};

export const px = (value: unknown): string | undefined => (typeof value === 'number' ? `${value}px` : typeof value === 'string' ? value : undefined);

export type TPaletteState = Pick<TViewCommonOption, 'isError' | 'isDisabled'>;

/**
 * A palette that may carry per-state variants of itself (`error`, `disabled`).
 * Each variant is a partial palette that overrides only the properties it needs.
 */
type TStatefulPalette<P> = P & {
    error?: Partial<P>;
    disabled?: Partial<P>;
};

/**
 * Resolves a palette property taking the active widget state into account.
 * Precedence: the matching state variant (`error` → `disabled`) wins when it defines
 * the property; otherwise the base palette value is used.
 */
export const getValueFromPalette = <P, K extends keyof P>(palette: TStatefulPalette<P>, propertyName: K, state: TPaletteState = {}): P[K] => {
    const statePalette = state.isError ? palette.error : state.isDisabled ? palette.disabled : undefined;

    return (statePalette?.[propertyName] ?? palette[propertyName]) as P[K];
};
